import React from 'react';
import { Div } from '@pinpoint/design-kit';

import Cell from 'src/infographics/roi_calculator_v2/cell';
import { formatNumber, formatNumberDollars } from '../../functions';

/* eslint-disable react/prop-types */

function InputTable({
	modelType,
	riskModel,
	riskModelDetail,
	acquisitionRate,
	averagePremium,
	carrierNonRenewalRate,
	policiesInForce,
	riskCost,
	yearlyCancellationRate,
	output,
	onChange,
	inputRefs,
	disableInput,
}) {
	return (
		<Div d="flex" w="50%" flexDir="column">
			<Div d="grid" flexDir="column" style={{ gridTemplateColumns: '1fr 1fr', gap: '1px' }}>
				<Cell
					pad="header"
					inputValue="Model Inputs"
					textWeight="700"
					justify="start"
					textSize="body"
					textColor="white"
					backgroundColor="brand500"
				/>
				<Cell
					pad="header"
					inputValue="Client Enters"
					textWeight="700"
					textSize="body"
					textColor="white"
					backgroundColor="brand500"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					inputValue="Risk model"
					textWeight="700"
					justify="start"
					textSize="caption"
					textColor="shade4D"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					type="text"
					textColor="shade4D"
					textAlign="right"
					inputValue={modelType.includes('ACT_') ? riskModelDetail : riskModel}
					textWeight="700"
					textSize="caption"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					textColor="shade4D"
					inputValue={
						['Early Cancelation', 'Early Cancellation'].includes(riskModel)
							? 'Total yearly losses from early cancellation'
							: 'Yearly direct loss and adjustment expenses'
					}
					textWeight="700"
					justify="start"
					textSize="caption"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					textColor="shade4D"
					type="input"
					disableInput={disableInput}
					textAlign="right"
					inputValue={riskCost}
					textWeight="700"
					inputPath="riskCost"
					format="currency"
					inputRefs={inputRefs}
					idx={0}
					onChange={onChange}
					InputProps={{
						// NOTE: passing input props for ProductFruits broke the input,
						// so we need to pass text props here
						'data-test': 'risk-cost-input',
						textColor: 'shade4D',
						textAlign: 'right',
						textWeight: '700',
					}}
				/>
				<Cell
					backgroundColor="skyBlueLight"
					textColor="shade4D"
					inputValue="Policies in force"
					justify="start"
					textWeight="700"
					textSize="caption"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					format="numeric"
					inputPath="policiesInForce"
					inputValue={policiesInForce}
					textWeight="700"
					onChange={onChange}
					inputRefs={inputRefs}
					idx={1}
					textAlign="right"
					textColor="shade4D"
					type="input"
					disableInput={disableInput}
				/>
				<Cell
					backgroundColor="skyBlueLight"
					textColor="shade4D"
					inputValue="Average yearly premium"
					justify="start"
					textWeight="700"
					textSize="caption"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					type="input"
					disableInput={disableInput}
					textColor="shade4D"
					textAlign="right"
					textWeight="700"
					inputValue={averagePremium}
					format="currency"
					inputRefs={inputRefs}
					idx={2}
					inputPath="averagePremium"
					onChange={onChange}
				/>
				<Cell
					backgroundColor="skyBlueLight"
					textColor="shade4D"
					inputValue="Yearly cancellation rate"
					justify="start"
					textWeight="700"
					textSize="caption"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					type="input"
					disableInput={disableInput}
					textColor="shade4D"
					textAlign="right"
					textWeight="700"
					inputValue={yearlyCancellationRate}
					format="percentage"
					inputRefs={inputRefs}
					idx={3}
					inputPath="yearlyCancellationRate"
					onChange={onChange}
				/>
				<Cell
					backgroundColor="skyBlueLight"
					textColor="shade4D"
					inputValue="Carrier non-renewal rate"
					justify="start"
					textWeight="700"
					textSize="caption"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					type="input"
					disableInput={disableInput}
					textColor="shade4D"
					textAlign="right"
					textWeight="700"
					inputValue={carrierNonRenewalRate}
					format="percentage"
					inputRefs={inputRefs}
					idx={4}
					inputPath="carrierNonRenewalRate"
					onChange={onChange}
				/>
				<Cell
					backgroundColor="skyBlueLight"
					textColor="shade4D"
					inputValue="New-business rate"
					justify="start"
					textWeight="700"
					textSize="caption"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					type="input"
					disableInput={disableInput}
					textColor="shade4D"
					textAlign="right"
					inputValue={acquisitionRate}
					textWeight="700"
					format="percentage"
					inputRefs={inputRefs}
					idx={5}
					inputPath="acquisitionRate"
					onChange={onChange}
				/>
				<Cell
					backgroundColor="skyBlueLight"
					textColor="shade4D"
					inputValue="Net growth rate"
					justify="start"
					textWeight="700"
					textSize="caption"
				/>
				<Cell
					backgroundColor="skyBlueLight"
					type="text"
					textAlign="right"
					textColor="shade4D"
					textWeight="700"
					format="percentage"
					inputValue={output.y1.netGrowthRate}
					formattingOptions={{
						allowNegative: false,
					}}
					textSize="body"
				/>
				<Cell
					backgroundColor="skyBlue2"
					textColor="shade4D"
					inputValue="Baseline premiums less risk cost"
					justify="start"
					textWeight="700"
					textSize="body"
				/>
				<Cell
					backgroundColor="skyBlue2"
					type="text"
					textAlign="right"
					textColor="black"
					textWeight="700"
					format="currency"
					inputValue={output.y1.baselineLessRiskCost}
					formattingOptions={{
						allowNegative: false,
					}}
					textSize="body"
				/>
				<Cell
					backgroundColor="skyBlue2"
					textColor="shade4D"
					inputValue="Pinpoint premiums less risk cost"
					justify="start"
					textWeight="700"
					textSize="body"
				/>
				<Cell
					backgroundColor="skyBlue2"
					type="text"
					textAlign="right"
					textColor="black"
					textWeight="700"
					format="currency"
					inputValue={output.y1.pinpointLessRiskCost}
					formattingOptions={{
						allowNegative: false,
					}}
					textSize="body"
				/>
			</Div>
			{modelType.includes('ACT_') && (
				<Div d="grid" flexDir="column" style={{ gridTemplateColumns: '1fr 0.5fr 0.5fr', gap: '1px' }} p={{ t: '1px' }}>
					<Cell
						backgroundColor="greenDarkPale"
						inputValue="Year-1 Client Savings"
						justify="start"
						textWeight="700"
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="percentagePoints"
						DivPropsProp={{
							'data-test': 'y1-lrr',
						}}
						inputValue={output?.y1?.lossRatioReduction ?? 0}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="currency"
						DivPropsProp={{
							'data-test': 'y1-save-val',
						}}
						inputValue={output?.y1?.costSavings ?? 0}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
						IconProps={{
							name: 'Info',
							size: '.5rem',
							color: 'white',
							m: { r: '.5em' },
							'data-tip': `New business: ${formatNumberDollars(
								Math.round(output.y1.newBusinessImpact)
							)}<br /><br />Non-renewal: ${formatNumberDollars(
								Math.round(output.y1.nonRenewalImpact)
							)}<br /><br />Cancellation: ${formatNumberDollars(Math.round(output.y1.cancellationImpact))}`,
						}}
					/>
					<Cell
						backgroundColor="greenDarkPale"
						inputValue="Year-2 Client Savings"
						justify="start"
						textWeight="700"
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="percentagePoints"
						DivPropsProp={{
							'data-test': 'y2-lrr',
						}}
						inputValue={output?.y2?.lossRatioReduction ?? 0}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="currency"
						DivPropsProp={{
							'data-test': 'y2-save-val',
						}}
						inputValue={output?.y2?.costSavings ?? 0}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
						IconProps={{
							name: 'Info',
							size: '.5rem',
							color: 'white',
							m: { r: '.5em' },
							'data-tip': `New business: ${formatNumberDollars(
								Math.round(output.y2.newBusinessImpact)
							)}<br /><br />Non-renewal: ${formatNumberDollars(
								Math.round(output.y2.nonRenewalImpact)
							)}<br /><br />Cancellation: ${formatNumberDollars(Math.round(output.y2.cancellationImpact))}`,
						}}
					/>
					<Cell
						backgroundColor="greenDarkPale"
						inputValue="Year-3 Client Savings"
						justify="start"
						textWeight="700"
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="percentagePoints"
						DivPropsProp={{
							'data-test': 'y3-lrr',
						}}
						inputValue={output?.y3?.lossRatioReduction ?? 0}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="currency"
						DivPropsProp={{
							'data-test': 'y3-save-val',
						}}
						inputValue={output?.y3?.costSavings ?? 0}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
						IconProps={{
							name: 'Info',
							size: '.5rem',
							color: 'white',
							m: { r: '.5em' },
							'data-tip': `New business: ${formatNumberDollars(
								Math.round(output.y3.newBusinessImpact)
							)}<br /><br />Non-renewal: ${formatNumberDollars(
								Math.round(output.y3.nonRenewalImpact)
							)}<br /><br />Cancellation: ${formatNumberDollars(Math.round(output.y3.cancellationImpact))}`,
						}}
					/>
					<Cell
						backgroundColor="greenDarkPale"
						inputValue="Total Impact after Year 3"
						justify="start"
						textWeight="700"
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="percentagePoints"
						DivPropsProp={{
							'data-test': 'ttl-lrr',
						}}
						inputValue={output?.y3?.lossRatioReduction ?? 0}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="currency"
						DivPropsProp={{
							'data-test': 'ttl-save-val',
						}}
						inputValue={output?.cumulative?.y3Impact ?? 0}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
						IconProps={{
							name: 'Info',
							size: '.5rem',
							color: 'white',
							m: { r: '.5em' },
							'data-tip': `New business: ${formatNumberDollars(
								Math.round(output.cumulative.newBusinessImpact)
							)}<br /><br />Non-renewal: ${formatNumberDollars(
								Math.round(output.cumulative.nonRenewalImpact)
							)}<br /><br />Cancellation: ${formatNumberDollars(Math.round(output.cumulative.cancellationImpact))}`,
						}}
					/>
				</Div>
			)}
			{modelType === 'CLASSIFIER' && (
				<Div d="grid" flexDir="column" style={{ gridTemplateColumns: '1fr 1fr', gap: '1px' }} p={{ t: '1px' }}>
					<Cell
						backgroundColor="greenDarkPale"
						inputValue="First-year client savings"
						justify="start"
						textWeight="700"
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="currency"
						inputValue={output.y1.costSavings}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
						DivPropsProp={{
							'data-test': '1y-save-val',
						}}
						IconProps={{
							name: 'Info',
							size: '.5rem',
							color: 'white',
							m: { r: '.5em' },
							'data-tip': `Targeted growth: $${formatNumber(
								Math.round(output.y1.targetedGrowthStrategy)
							)}<br />Renewal: $${formatNumber(Math.round(output.y1.renewalStrategy))}`,
						}}
					/>
					<Cell
						backgroundColor="greenDarkPale"
						inputValue="First-year loss ratio reduction"
						justify="start"
						textWeight="700"
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="percentagePoints"
						DivPropsProp={{
							'data-test': '1y-lr-val',
						}}
						inputValue={output.y1.percentagePoints}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDarkPale"
						inputValue="Three-year client savings >"
						justify="start"
						textWeight="700"
						textSize="body"
						textColor="white"
					/>
					<Cell
						backgroundColor="greenDark2"
						textAlign="right"
						textWeight="700"
						format="currency"
						DivPropsProp={{
							'data-test': '3y-save-val',
						}}
						inputValue={output.y1.costSavingsTriple}
						formattingOptions={{
							allowNegative: false,
						}}
						textSize="body"
						textColor="white"
						IconProps={{
							name: 'Info',
							size: '.5rem',
							color: 'white',
							m: { r: '.5em' },
							'data-tip': `Targeted growth: $${formatNumber(
								Math.round(output.y1.targetedGrowthStrategyTriple)
							)}<br />Renewal: $${formatNumber(Math.round(output.y1.renewalStrategyTriple))}`,
						}}
					/>
				</Div>
			)}
		</Div>
	);
}

export default InputTable;
